import { type CartType } from '@jsmdg/cart-library-ts';
import {
    type AvailabilitiesCalendarSubtype,
    type CheckoutCategorySubtype,
    type CheckoutStep,
    ClickElement,
    ClickText,
    ClickType,
    GA4EventName,
    GA4FeatureCategory,
    trackAnalyticsEvent,
    trackEecAddToCart,
    trackEecCheckout,
    trackEecProductDetail,
} from '@jsmdg/tracking';
import { ProductPromotionType, ProductPromotionTypeTrackingLabel } from '../../../shared/enums';
import { isValueVoucher, isValueVoucherWithPriceRange } from '../../../shared/product-safeguards';
import { type Location, type Price, type Product } from '../../../shared/types';
import { getPurchaseVariantForTracking } from '../../helper/getPurchaseType';
import { mapToTrackingCart } from '../../mapper/mapToTrackingCart';
import { type PurchaseType } from '../../types/enums/purchaseType';

async function trackAddToCartClicked(
    clickType: ClickType = ClickType.DefaultUpper,
    clickText: ClickText | string = ClickText.AddToCart,
    categorySubtype?: string,
): Promise<void> {
    await window.yieldToMainThread();
    trackAnalyticsEvent({
        category: 'ProductDetail',
        action: 'ButtonClick',
        label: 'AddToCart',
        eventData: {
            eventName: GA4EventName.ClickEcomCta,
            feature_category: GA4FeatureCategory.ProductdetailBuybox,
            click_element: ClickElement.AddToCart,
            click_text: clickText,
            click_type: clickType,
            category_subtype: categorySubtype,
        },
    });
}

async function trackRatingClicked(): Promise<void> {
    await window.yieldToMainThread();
    trackAnalyticsEvent({
        category: 'ProductDetail',
        action: 'TextClick',
        label: 'Ratings',
        eventData: {
            eventName: GA4EventName.ClickRating,
            feature_category: GA4FeatureCategory.ProductdetailBuybox,
        },
    });
}

function getNudge(product: Product) {
    return product.productNudge
        ? {
              id: product.productNudge?.id,
              text: product.productNudge?.text,
          }
        : undefined;
}

function getParticipants(product: Product) {
    if (isValueVoucher(product) || isValueVoucherWithPriceRange(product)) {
        return {};
    }

    return {
        participants: {
            min: product.minParticipants,
            max: product.maxParticipants,
        },
    };
}

function getPromotion(product: Product) {
    return product.promotionType !== ProductPromotionType.None
        ? ProductPromotionTypeTrackingLabel[product.promotionType]
        : undefined;
}

function trackEECAddToCartAdded(
    product: Product,
    price: Price,
    locations: Location[],
    quantity: number,
    hasFriendsDiscount: boolean,
    purchaseType: PurchaseType | undefined,
    categorySubType?: CheckoutCategorySubtype | AvailabilitiesCalendarSubtype,
): void {
    trackEecAddToCart({
        product: {
            id: product.productId,
            url: product.url,
            trackingName: product.trackingName,
            rating: product.rating,
            manufacturerSku: product.manufacturerSku,
            trackingPath: product.trackingPath,
            price,
            originalPrice: product.originalPrice,
            title: product.title,
            discountBadgeId: product.discountBadge?.id,
            discountBadgeName: product.discountBadge?.text,
            promotion: getPromotion(product),
            locationCount: locations.length,
            ...getParticipants(product),
            hasFriendsDiscount,
            nudge: getNudge(product),
            hasVideo: product.videos.length > 0,
        },
        quantity,
        variant: getPurchaseVariantForTracking(product.type, purchaseType),
        categoryType: categorySubType,
    });
}

function trackEECProductDetailViewed(
    product: Product,
    price: Price,
    locations: Location[],
    purchaseType: PurchaseType | undefined,
    minParticipants: number | undefined,
    maxParticipants: number | undefined,
    bookableLocationsCount: number | undefined,
): void {
    trackEecProductDetail(
        {
            id: product.productId,
            trackingName: product.trackingName,
            manufacturerSku: product.manufacturerSku,
            trackingPath: product.trackingPath,
            price: { gross: { ...price.gross } },
            originalPrice: product.originalPrice
                ? { gross: { ...product.originalPrice.gross } }
                : undefined,
            title: product.title,
            discountBadgeId: product.discountBadge?.id,
            discountBadgeName: product.discountBadge?.text,
            promotion: getPromotion(product),
            rating: product.rating,
            locationCount: locations.length,
            participants: {
                min: minParticipants ?? 0,
                max: maxParticipants ?? 0,
            },
            nudge: getNudge(product),
            hasVideo: product.videos.length > 0,
            isTicketAvailable: Boolean(bookableLocationsCount),
        },
        getPurchaseVariantForTracking(product.type, purchaseType),
    );
}

function trackBuyNowEecCheckout(
    cart: CartType,
    checkoutStep: CheckoutStep,
    categoryType: CheckoutCategorySubtype,
): void {
    trackEecCheckout(mapToTrackingCart(cart), checkoutStep, categoryType);
}

export {
    trackAddToCartClicked,
    trackBuyNowEecCheckout,
    trackEECAddToCartAdded,
    trackEECProductDetailViewed,
    trackRatingClicked,
};
